import { AreaChartProps, AreaChartSeries, LineChartProps, LineChartSeries } from '@mantine/charts'
import { Box, Flex } from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import { useMemo, useRef } from 'react'

import useDateFormatter from '@/hooks/useDateFormatter'
import { CoinPrices, PriceIndex } from '@/types/coin'

import styles from '../index.module.css'
import {
  activeDotProps,
  baseAnimationProps,
  baseTickProps,
  baseYaxisProps,
  CHART_HEIGHT,
  getPercentChangeColor,
  getYAxisTicks,
} from '../utils'

const CURRENT_PRICE_SERIES_NAME = 'currentCoinPrice'
const BTC_PRICE_SERIES_NAME = 'btcCoinPrice'

export interface LineData {
  timestamp: number
  currentCoinPrice: number
  btcCoinPrice?: number
}

export interface UseCoinLineChartProps {
  btcPricesVisible: boolean
  currentCoinPrices?: CoinPrices
  btcCoinPrices?: CoinPrices
  /**
   * when mouse move out of the chart payload would be null
   */
  onShowPriceTooltip?: (payload: LineData | undefined) => void
}

function useLineData({
  currentCoinPrices,
  btcCoinPrices,
}: Omit<UseCoinLineChartProps, 'onShowPriceTooltip' | 'btcPricesVisible'>) {
  return useMemo<LineData[]>(() => {
    if (!currentCoinPrices) return []
    const data = currentCoinPrices.prices.map(([timestamp, price]) => ({
      timestamp,
      [CURRENT_PRICE_SERIES_NAME]: price,
    }))
    if (!btcCoinPrices) return data
    return data.map((coin, index) => ({
      ...coin,
      [BTC_PRICE_SERIES_NAME]: btcCoinPrices.prices[index]?.[PriceIndex.price],
    }))
  }, [currentCoinPrices, btcCoinPrices])
}

function useBtcAndCurrentPriceYaxisProps({
  currentCoinPrices,
  btcCoinPrices,
}: Pick<UseCoinLineChartProps, 'currentCoinPrices' | 'btcCoinPrices'>) {
  const btcYaxisTicks = useMemo(() => {
    if (!btcCoinPrices) return []
    const prices = btcCoinPrices.prices.map(price => price[PriceIndex.price])
    return getYAxisTicks(prices)
  }, [btcCoinPrices])

  const currentPriceYaxisTicks = useMemo(() => {
    if (!currentCoinPrices) return []
    const prices = currentCoinPrices.prices.map(price => price[PriceIndex.price])
    return getYAxisTicks(prices)
  }, [currentCoinPrices])

  const currentPriceYaxisProps = useMemo(
    () => ({
      ...baseYaxisProps,
      tick: {
        ...baseTickProps,
        transform: `translate(-8, ${currentPriceYaxisTicks.length === 1 ? '-10' : '0'})`,
        textAnchor: 'end',
      },
      yAxisId: CURRENT_PRICE_SERIES_NAME,
      ticks: currentPriceYaxisTicks,
      height: undefined,
      width: undefined,
      scale: undefined,
      padding: { top: 15, bottom: 15 },
      tickLine: false,
      range: undefined,
    }),
    [currentPriceYaxisTicks],
  )

  const btcPriceYaxisProps = useMemo(
    () => ({
      ...baseYaxisProps,
      tick: {
        ...baseTickProps,
        transform: 'translate(10, 0)',
        textAnchor: 'start',
      },
      ticks: btcYaxisTicks,
      yAxisId: BTC_PRICE_SERIES_NAME,
    }),
    [btcYaxisTicks],
  )

  return {
    btcPriceYaxisProps,
    currentPriceYaxisProps,
  }
}

function useCoinLineSeries(showBtcPrice: boolean, currentCoinLineColor: string) {
  return useMemo<(LineChartSeries & AreaChartSeries)[]>(() => {
    const series: (LineChartSeries & AreaChartSeries)[] = [
      {
        name: CURRENT_PRICE_SERIES_NAME,
        color: currentCoinLineColor,
        yAxisId: CURRENT_PRICE_SERIES_NAME,
      },
    ]
    if (showBtcPrice) {
      series.push({
        name: BTC_PRICE_SERIES_NAME,
        color: 'var(--content-tertiary)',
        yAxisId: BTC_PRICE_SERIES_NAME,
      })
    }
    return series
  }, [showBtcPrice, currentCoinLineColor])
}

const baseChartProps: Omit<LineChartProps & AreaChartProps, 'series' | 'data'> = {
  gridAxis: 'none',
  withXAxis: false,
  curveType: 'linear',
  dotProps: {
    r: 0,
  },
  activeDotProps,
  dataKey: 'timestamp',
  h: CHART_HEIGHT,
  withRightYAxis: true,
}

const areaChartBaseProps: Partial<AreaChartProps> & Pick<AreaChartProps, 'dataKey'> = {
  ...baseChartProps,
  areaProps: {
    ...baseAnimationProps,
  },
  areaChartProps: {
    margin: {
      right: -60,
    },
  },
}

const lineChartBaseProps: Partial<LineChartProps> & Pick<LineChartProps, 'dataKey'> = {
  ...baseChartProps,
  lineProps: {
    isAnimationActive: baseAnimationProps.isAnimationActive,
    animationEasing: baseAnimationProps.animationEasing,
    animationDuration: baseAnimationProps.animationDuration,
  },
  lineChartProps: {
    margin: {
      top: 0,
      right: -60,
      bottom: 0,
      left: -60,
    },
  },
}

function useTooltipProps(_onShowPriceTooltip: UseCoinLineChartProps['onShowPriceTooltip']) {
  const lastTooltipTimestamp = useRef(0)
  const { formatDate } = useDateFormatter()
  const onShowPriceTooltip = useDebouncedCallback((payload: LineData | undefined) => {
    _onShowPriceTooltip?.(payload)
  }, 20)
  return useMemo(
    () => ({
      cursor: {
        stroke: 'var(--border-status-active)',
        strokeWidth: 1,
      },
      offset: -54,
      content: (props: {
        label?: string | number
        payload?: Array<{
          payload?: LineData
        }>
      }) => {
        const currentTimeStamp = ((props.label as number) * 1000) as number
        // when do not have this, means mouse move out of chart
        if (!props.payload?.length) {
          onShowPriceTooltip(void 0)
          lastTooltipTimestamp.current = 0
        } else if (props.payload?.[0]?.payload) {
          const currentPointData = props.payload[0].payload

          // for optimize do not trigger by the same point
          if (lastTooltipTimestamp.current !== currentPointData.timestamp) {
            onShowPriceTooltip(currentPointData)
            lastTooltipTimestamp.current = currentPointData.timestamp
          }
        }
        return (
          <Flex className={styles.tooltip}>
            <Box>{formatDate(currentTimeStamp, 'h:mm').toLowerCase()}</Box>
            <Box>
              {formatDate(currentTimeStamp, 'ddd') + ' '}
              {formatDate(currentTimeStamp, 'MMM D,YYYY')}
            </Box>
          </Flex>
        )
      },
    }),
    [],
  )
}

export function useCoinLineChartProps({
  btcPricesVisible,
  currentCoinPrices,
  btcCoinPrices,
  onShowPriceTooltip,
}: UseCoinLineChartProps) {
  const data = useLineData({
    currentCoinPrices,
    btcCoinPrices,
  })

  const { currentPriceYaxisProps, btcPriceYaxisProps } = useBtcAndCurrentPriceYaxisProps({
    currentCoinPrices,
    btcCoinPrices,
  })

  const currentCoinLineColor = useMemo(() => {
    if (!currentCoinPrices?.price_change) return ''
    return getPercentChangeColor(currentCoinPrices.price_change)
  }, [currentCoinPrices?.price_change])
  const series = useCoinLineSeries(btcPricesVisible, currentCoinLineColor)
  const tooltipProps = useTooltipProps(onShowPriceTooltip)

  return useMemo<LineChartProps | AreaChartProps>(
    () => ({
      ...(btcPricesVisible ? lineChartBaseProps : areaChartBaseProps),
      data,
      series,
      withYAxis: btcPricesVisible,
      yAxisProps: btcPriceYaxisProps,
      rightYAxisProps: currentPriceYaxisProps,
      tooltipProps,
      // this config inorder to disable the CartesianGrid render
      gridProps: {
        vertical: false,
        verticalPoints: [0],
      },
      withDots: false,
    }),
    [data, series, btcPricesVisible, btcPriceYaxisProps, currentPriceYaxisProps, tooltipProps],
  )
}
